import React from 'react';
import Konva from 'konva';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
	BG_IMAGE_MODES,
	BG_NOISES,
	BG_OPACITY_SLIDER_MARKS,
	COMMANDS,
	DEFAULT_FONT_SIZE,
	DEFAULT_STAGE_MAX_WIDTH,
	FONT_FAMILIES,
	FONT_SIZE_LIST,
	FONT_STROKE_COLORS,
	PROMOTION,
	SITE_NAME,
	STORAGE_KEY_BGURL,
	STORAGE_KEY_TEXT,
	t_storageKeys,
} from './const';
import { loadImage } from './UtilsDom';
import { flatJoin } from './UtilsDataControl';
import { useDebouncedCallback } from 'use-debounce';
import { Route, Routes } from 'react-router-dom';
import ScreenHome from './ScreenHome';
import ScreenEdit from './ScreenEdit';

export type t_loading = 'LOADING' | 'SUCCESS' | 'FAILED' | 'IDLING';

export default function Product() {
	const [state_textForForm, set_textForForm] = React.useState<string>(
		R.defaultTo(R.always(''))(getDataFromStorage(STORAGE_KEY_TEXT)),
	);
	const [state_textForStage, set_textForStage] =
		React.useState<string>(state_textForForm);
	const [state_imgurUrl, set_imgurUrl] = React.useState<string>('');
	const [state_bgImageLoading, set_bgImageLoading] =
		React.useState<t_loading>('IDLING');
	const [state_bgLocalFileBase64, set_bgLocalFileBase64] = React.useState<
		string | undefined
	>(); //使い道なくなったが、クッキーで使えるかも？
	const [state_bgUrl, set_bgUrl] = React.useState<string>(
		R.defaultTo(R.always(''))(getDataFromStorage(STORAGE_KEY_BGURL)),
	);
	const [state_bgElement, set_bgElement] = React.useState<
		HTMLImageElement | undefined
	>();
	const [state_bgImageModeListIndex, set_bgImageModeListIndex] =
		React.useState<number>(0);
	const [state_bgNoiseIndex, set_bgNoiseIndex] = React.useState<number>(0);
	const [state_bgNoiseImage, set_bgNoiseImage] = React.useState<
		HTMLImageElement | undefined
	>();
	const [state_bgOpacity, set_bgOpacity] = React.useState<number>(1);
	const [state_bgColor, set_bgColor] = React.useState<string>('#ffffff');
	const installedFontFamilies: string[] = React.useMemo(
		() => flatJoin(',', filterInstalledFont(FONT_FAMILIES)),
		[],
	);
	const [state_fontFamily, set_fontFamily] = React.useState<string>(
		installedFontFamilies[0],
	);
	const [state_fontSize, set_fontSize] =
		React.useState<number>(DEFAULT_FONT_SIZE);
	const [state_fontColor, set_fontColor] = React.useState<string>('#000000');
	/*
	const [state_fontColor, set_fontColor] = React.useState<string>(
		FONT_COLORS[0],
	);
	*/
	const [state_fontStrokeColorListIndex, set_fontStrokeColorListIndex] =
		React.useState<number>(0);
	const [state_lineHeight, set_lineHeight] = React.useState<number>(1.5);
	//const [state_origImageW, set_origImageW] = React.useState<number>(100);
	//const [state_origImageH, set_origImageH] = React.useState<number>(100);
	const [state_stageWidth, set_stageWidth] = React.useState<number>(0);
	const ref_stage = React.useRef<Konva.Stage>(null); //画像化に使う
	//一定時間後にBGURLを更新。
	const debouncedSetBg = useDebouncedCallback(
		(url: string) => {
			setBgImageData(url);
			setDataToStorage(STORAGE_KEY_BGURL, url);
			//console.log({ 'save storage': url });
		},
		// delay in ms
		777,
	);
	const debouncedSetText = useDebouncedCallback(
		(text: string) => {
			set_textForStage(text);
			setDataToStorage(STORAGE_KEY_TEXT, text);
			//console.log({ 'save storage': text });
		},
		// delay in ms
		1000,
	);
	/*
	console.log({
		storage: [
			getDataFromStorage(STORAGE_KEY_TEXT),
			getDataFromStorage(STORAGE_KEY_BGURL),
		],
	});*/

	//初回訪問時
	React.useEffect(() => {
		//ウィンドウサイズがDEFAULT_STAGE_MAX_WIDTHよりも小さければ、ウィンドウサイズ-5%をステ―ジのデフォのサイズにする。
		set_stageWidth(
			R.ifElse<any, number, number>(
				(e) => DEFAULT_STAGE_MAX_WIDTH > e,
				(e) => Math.floor(e * 0.95),
				R.always(DEFAULT_STAGE_MAX_WIDTH),
			)(window.innerWidth),
		);

		setBgImageData(state_bgUrl);
		setBgNoise(state_bgNoiseIndex);
		/*
		const windowWidth = window.innerWidth;
		set_stageWidth(
			DEFAULT_STAGE_MAX_WIDTH > windowWidth //ウィンドウサイズがDEFAULT_STAGE_MAX_WIDTHよりも小さければ、ウィンドウサイズ-5%をステ―ジのデフォのサイズにする。
				? Math.floor(windowWidth * 0.95)
				: DEFAULT_STAGE_MAX_WIDTH,
		);*/
	}, []);

	/**
	 * テキスト更新
	 * 第一引数：末尾に不可するテキスト。
	 * 第二引数：新しいテキスト。指定が無ければ現在のテキストになる。
	 */
	const setTextWithDebounceAdd = React.useCallback(
		(add?: string) => (newText?: string | Event) => {
			set_textForForm((currentText) => {
				const baseText = typeof newText !== 'string' ? currentText : newText;
				const _add = add ?? '';
				const nextText = baseText + _add;
				/*
				console.log({
					add: add,
					newText: newText,
					currentText: currentText,
					baseText: baseText,
					_add: _add,
					nextText: nextText,
				});*/
				debouncedSetText(nextText);
				return nextText;
			});
		},
		[],
	);

	const onFontSizeChange = React.useCallback(
		(value: string) => set_fontSize(Number(value)),
		[],
	);

	/**
	 * 画像が読み込まれた後に実行されるので実質これが終わった時が読み込み終了時
	 * @param url
	 * @returns
	 */
	const setBgImageData = async (url: string) => {
		if (R.isEmpty(url)) {
			set_bgElement(undefined);
			set_bgImageLoading('IDLING');
			return;
		}
		set_bgImageLoading('LOADING');
		const res = await loadImage(url).catch((e) => {
			return;
		});
		if (!res) {
			set_bgElement(undefined);
			set_bgImageLoading('FAILED');
			return;
		}
		set_bgElement(res);
		set_bgImageLoading('SUCCESS');
	};

	const updateBgUrl = React.useCallback((value: string) => {
		set_bgLocalFileBase64(undefined);
		const url = commandToUrl(value);
		set_bgUrl(url);
		debouncedSetBg(url);
	}, []);

	const updateBgBase64 = React.useCallback((value: string) => {
		set_bgUrl('');
		set_bgLocalFileBase64(value);
		setBgImageData(value);
		removeDataFromStorage(STORAGE_KEY_BGURL);
	}, []);

	const setBgNoise = async (index: number) => {
		set_bgNoiseIndex(index);
		const filename = BG_NOISES[index].value;
		const res =
			filename === ''
				? false
				: await loadImage(filename).catch((e) => {
						return;
				  });
		if (!res) {
			set_bgNoiseImage(undefined);
			return;
		}
		set_bgNoiseImage(res);
	};

	const stageToDataUrl = React.useCallback(
		(): string | undefined =>
			ref_stage.current?.toDataURL({
				mimeType: 'image/jpeg',
				quality: 0.8,
			}),
		[],
	);

	/*
	const fontStrokeColorValue = React.useMemo(
		() =>
			R.pipe(
				R.find<t_fontStrokeColor>(R.propEq('name', state_fontStrokeColorName)),
				R.defaultTo(FONT_STROKE_COLORS[0]),
				R.prop('value'),
			)(FONT_STROKE_COLORS),
		[state_fontStrokeColorName],
	);
*/

	return (
		<Box mb={4}>
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Typography gutterBottom variant='h6' component='h1'>
					{SITE_NAME}
				</Typography>
			</Box>
			<Routes>
				<Route
					path='/'
					element={
						<ScreenHome
							propsTextForm={{
								value: state_textForForm,
								onChange: setTextWithDebounceAdd(),
							}}
							addSiteInfo={setTextWithDebounceAdd('\n\n' + PROMOTION)}
						/>
					}
				/>
				<Route
					path='/edit'
					element={
						<ScreenEdit
							propsSaveButton={{
								selectedModeIndex: state_bgImageModeListIndex,
								changeMode: set_bgImageModeListIndex,
								stageToDataUrl: stageToDataUrl,
							}}
							propsImgurButton={{
								imgurUrl: state_imgurUrl,
								onGetImgurUrl: set_imgurUrl,
								stageToDataUrl: stageToDataUrl,
							}}
							bgImageLoading={state_bgImageLoading}
							propsBgImageSelectTabs={{
								url: state_bgUrl,
								onUrlChange: updateBgUrl,
								onFileImageLoaded: updateBgBase64,
							}}
							propsBgImageModeRadioButton={{
								items: BG_IMAGE_MODES,
								onChange: set_bgImageModeListIndex,
								selectedItemIndex: state_bgImageModeListIndex,
							}}
							propsBgOpacitySlider={{
								value: state_bgOpacity,
								onChange: set_bgOpacity,
								marks: BG_OPACITY_SLIDER_MARKS,
							}}
							propsBgColorInput={{
								value: state_bgColor,
								onInput: set_bgColor,
							}}
							propsBgNoiseRadioButton={{
								items: BG_NOISES,
								onChange: setBgNoise,
								selectedItemIndex: state_bgNoiseIndex,
							}}
							propsFontFamilySelector={{
								items: installedFontFamilies,
								value: state_fontFamily,
								onChange: set_fontFamily,
							}}
							propsFontSizeSelector={{
								items: FONT_SIZE_LIST,
								value: String(state_fontSize),
								onChange: onFontSizeChange,
							}}
							propsFontColorInput={{
								value: state_fontColor,
								onInput: set_fontColor,
							}}
							/*
							propsColorPicker={{
								colorList: FONT_COLORS,
								selectedColor: state_fontColor,
								onChange: set_fontColor,
							}}
							*/
							propsFontStrokeColorRadioButton={{
								items: FONT_STROKE_COLORS,
								onChange: set_fontStrokeColorListIndex,
								selectedItemIndex: state_fontStrokeColorListIndex,
							}}
							propsLineHeightForm={{
								value: state_lineHeight,
								onChange: set_lineHeight,
							}}
							propsStageWidth={{
								value: state_stageWidth,
								onChange: set_stageWidth,
							}}
							propsCanvas={{
								fontFamily: state_fontFamily,
								fontSize: state_fontSize,
								fontColor: state_fontColor,
								fontStrokeColor:
									FONT_STROKE_COLORS[state_fontStrokeColorListIndex].value,
								lineHeight: state_lineHeight,
								stageWidth: state_stageWidth,
								text: state_textForStage,
								bgImage: state_bgElement,
								bgNoise: state_bgNoiseImage,
								bgOpacity: state_bgOpacity,
								bgColor: state_bgColor,
								ref_stage: ref_stage,
								mode: BG_IMAGE_MODES[state_bgImageModeListIndex].value,
								//ref_image: ref_image,
							}}
						/>
					}
				/>
			</Routes>
		</Box>
	);
}
/*
const WindowSize = () => {
	const { width, height } = useWindowSize();

	return (
		<div>
			<div>width: {width}</div>
			<div>height: {height}</div>
		</div>
	);
};*/

/**
 * fontListの各要素の内1つででもデバイスが所有しているフォントだけを残すfilter
 * @param fontList
 * @returns
 */
const filterInstalledFont = (fontList: string[][]) =>
	R.filter(R.any(isFontInstalled), fontList);

const isFontInstalled = (fontName: string) =>
	document.fonts.check('bold 12px ' + fontName);

const commandToUrl = (url: string): string =>
	R.defaultTo(url)(R.prop(url)(COMMANDS));

const getDataFromStorage = (key: t_storageKeys) => localStorage.getItem(key);
const setDataToStorage = (key: t_storageKeys, value: string) =>
	localStorage.setItem(key, value);
const removeDataFromStorage = (key: t_storageKeys) =>
	localStorage.removeItem(key);
